import { ActionTree } from 'vuex'

import api from '@/apiClient'

import {
  OrderPayment,
  OrderPaymentPayload,
  OrderPaymentFilters
} from '@/models'

import { mergeById } from '@/utils'

import { RootState } from '../../state'
import { OrderingState } from '../state'

import {
  ADD_INVOICE_ORDER_PAYMENTS,
  ADD_ORDER_PAYMENT,
  APPLY_ORDER_PAYMENT_FILTERS,
  INCREMENT_ORDER_PAYMENTS_QUERY_INDEX,
  LOADED_ALL_ORDER_PAYMENTS,
  RESET_LOADED_ALL_ORDER_PAYMENTS,
  RESET_ORDER_PAYMENTS_QUERY_INDEX,
  UPDATE_INVOICE_ORDER_PAYMENTS,
  UPDATE_INVOICE_ORDER_PAYMENTS_TOTAL,
  UPDATE_ORDER_PAYMENTS,
  UPDATE_ORDER_PAYMENTS_TOTAL,
  SET_ORDER_PAYMENTS_DEFAULT_FILTERS_FLAG
} from './mutations'

const actions: ActionTree<OrderingState, RootState> = {
  /**
   * Adds a new OrderPayment and updates an Invoice relation.
   *
   * It adds the Invoice relation if it is not found.
   *
   * @param orderPayment OrderPayment to be added.
   */
  addOrderPayment ({ commit, state }, orderPayment: OrderPayment) {
    const invoiceId = orderPayment.ownerId

    let invoiceOrderPayments = state.invoices.orderPayments[invoiceId]

    if (invoiceOrderPayments === undefined) {
      commit(ADD_INVOICE_ORDER_PAYMENTS, invoiceId)
      invoiceOrderPayments = state.invoices.orderPayments[invoiceId]
    }

    commit(ADD_ORDER_PAYMENT, orderPayment)

    commit(UPDATE_INVOICE_ORDER_PAYMENTS_TOTAL, {
      invoiceId,
      total: invoiceOrderPayments.total + 1
    })

    commit(UPDATE_INVOICE_ORDER_PAYMENTS, {
      invoiceId,
      orderPayments: [...invoiceOrderPayments.ids, orderPayment.id]
    })
  },

  /**
   * Applies OrderPaymentFilters to the query and resets the loading state.
   *
   * @param filters OrderPaymentFilters to be applied.
   */
  changeOrderPaymentFilter (
    { commit },
    { filters, isDefaultFilters }: { filters: OrderPaymentFilters; isDefaultFilters: boolean }
  ) {
    commit(APPLY_ORDER_PAYMENT_FILTERS, filters)

    commit(SET_ORDER_PAYMENTS_DEFAULT_FILTERS_FLAG, isDefaultFilters)

    commit(RESET_LOADED_ALL_ORDER_PAYMENTS)
    commit(RESET_ORDER_PAYMENTS_QUERY_INDEX)
  },

  /**
   * Loads one OrderPayment.
   *
   * It rejects if the user is not logged in.
   *
   * It loads the Invoice if it is not loaded.
   *
   * @param ownerId OrderPayment ownerId to be loaded.
   */
  async loadOrderPayment ({ commit, dispatch, getters, rootGetters, state }, ownerId: number) {
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    const orderPayment = await api.orderPayments.fetchOrderPayment(providerId, ownerId)

    let invoiceOrderPayments = state.invoices.orderPayments[ownerId]

    if (invoiceOrderPayments === undefined) {
      await (dispatch('loadInvoice', ownerId))
      invoiceOrderPayments = state.invoices.orderPayments[ownerId]
    }

    if (getters.orderPaymentByInvoiceId(ownerId) === null) {
      commit(UPDATE_ORDER_PAYMENTS_TOTAL, state.orderPayments.total + 1)
    }

    if (!invoiceOrderPayments.ids.has(orderPayment.id)) {
      await dispatch('addOrderPayment', orderPayment)
    } else {
      const orderPayments = mergeById(state.orderPayments.elements, [orderPayment])
      commit(UPDATE_ORDER_PAYMENTS, orderPayments)
    }
  },

  /**
   * Loads a list of OrderPayments.
   *
   * It rejects if the user is not logged in.
   *
   * It loads missing Invoices.
   *
   * It updates the Invoice relation for each loaded OrderPayment.
   */
  async loadOrderPaymentPage ({ commit, dispatch, rootGetters, getters, rootState, state }) {
    const token = rootState.auth.token
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (token === null || providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    try {
      const page = await api.orderPayments.fetchOrderPaymentPage(
        token,
        providerId,
        businessUnit.id,
        state.orderPayments.query
      )

      if (state.orderPayments.total !== page.total) {
        commit(UPDATE_ORDER_PAYMENTS_TOTAL, page.total)
      }

      if (page.elements.length > 0) {
        const newInvoices = page.elements
          .map((orderPayment) => orderPayment.ownerId)
          .filter((invoiceId) => getters.invoiceById(invoiceId) === null)

        if (newInvoices.length > 0) {
          await dispatch('loadInvoiceList', newInvoices)
        }

        const orderPayments = mergeById(state.orderPayments.elements, page.elements)

        commit(UPDATE_ORDER_PAYMENTS, orderPayments)

        const invoiceOrderPayments = page.elements.reduce((map, orderPayment) => {
          let ids = map.get(orderPayment.ownerId)

          if (ids === undefined) {
            ids = []
            map.set(orderPayment.ownerId, ids)
          }

          ids.push(orderPayment.id)
          return map
        }, new Map<number, number[]>())

        for (const [invoiceId, ids] of invoiceOrderPayments) {
          commit(UPDATE_INVOICE_ORDER_PAYMENTS, {
            invoiceId,
            orderPayments: [
              ...state.invoices.orderPayments[invoiceId].ids,
              ...ids
            ]
          })
        }

        if (getters.filteredOrderPayments.length === page.total) {
          commit(LOADED_ALL_ORDER_PAYMENTS)
        }

        commit(INCREMENT_ORDER_PAYMENTS_QUERY_INDEX, page.elements.length)
      } else {
        commit(RESET_ORDER_PAYMENTS_QUERY_INDEX)

        if (page.total === 0 && state.orderPayments.elements.length > 0) {
          commit(UPDATE_ORDER_PAYMENTS, [])
        }
      }
    } catch (error) {
      if (error.response) {
        const { status } = error.response
        if (status === 401) {
          dispatch('logout')
        }
      }
    }
  },

  /**
   * Inform a Payment.
   *
   * It rejects if the user is not logged in.
   *
   * @param payload Payload to inform it.
   */
  async informPayment (
    { dispatch, rootGetters, rootState },
    { id, method, ownerId }: OrderPaymentPayload
  ) {
    const token = rootState.auth.token
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (token === null || providerId === null) {
      throw new Error('User is not authenticated')
    }

    try {
      await api.orderPayments.latePayment(token, providerId, id, method)

      if (!rootGetters.isWebSocketConnected) {
        await dispatch('loadOrderPayment', ownerId)
      }
    } catch (error) {
      if (error.response) {
        const { status } = error.response
        if (status === 401) {
          dispatch('logout')
        }
      }
    }
  }
}

export default actions
